import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/api/client';
import { wsUrl } from '@/lib/workspace';
import type { ToolStep } from '@/types/tool';

// Quick ask (docs/agent-runtime.md "Fast lane") - a command-palette popover.
// It runs on the SINGLE streaming path every other surface uses: mint a
// `mode='fast'` conversation (POST /v1/api/ask), submit through
// POST /conversations/{id}/messages, and observe the turn live over
// GET /conversations/{id}/stream. No bespoke sync endpoint - the fast turn
// streams text + tool steps exactly like a Studio turn, just rendered compact.
// "New" resets the session; "Continue in Studio" promotes the conversation.

export interface QuickMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Assistant bubble still streaming (deltas/tools landing). */
  streaming?: boolean;
  /** Tool steps the fast turn ran, in order - same shape Studio uses, fed to
   *  the shared ToolFold renderer. */
  tools?: ToolStep[];
}

export const useQuickAskStore = defineStore('quickAsk', () => {
  const open = ref(false);
  const messages = ref<QuickMessage[]>([]);
  const conversationId = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  // The context-window guard tripped (a `context_full` error event): the
  // session is full and the only way forward is to promote it to Studio.
  const full = ref(false);

  // One live EventSource per open session, mirrored from Studio. Events route
  // to the current streaming assistant bubble; the journal is the source of
  // truth, so a reopen in Studio shows the same turn (tool steps included).
  let sse: EventSource | null = null;

  function closeStream(): void {
    if (sse) { sse.close(); sse = null; }
  }

  // Opening the popover always starts a FRESH quick ask - the previous chat is
  // a real conversation (saved, promotable), so we don't resurrect a stale
  // in-memory snapshot. Multi-turn within one open session still works.
  function openPanel(): void { reset(); open.value = true; }
  function closePanel(): void { open.value = false; closeStream(); }
  function toggle(): void {
    if (open.value) { open.value = false; closeStream(); }
    else { reset(); open.value = true; }
  }

  function reset(): void {
    closeStream();
    messages.value = [];
    conversationId.value = null;
    error.value = null;
    full.value = false;
    loading.value = false;
  }

  // The assistant bubble events currently flow into - the last one if it's a
  // live placeholder, else a fresh one (defensive; `send` pre-creates it).
  function currentAssistant(): QuickMessage {
    const last = messages.value[messages.value.length - 1];
    if (last && last.role === 'assistant' && last.streaming) return last;
    const m: QuickMessage = { role: 'assistant', text: '', streaming: true, tools: [] };
    messages.value.push(m);
    return m;
  }

  function onEvent(kind: string, ev: MessageEvent): void {
    let p: Record<string, unknown> = {};
    try { p = ev.data ? JSON.parse(ev.data) : {}; } catch { /* ignore */ }
    switch (kind) {
      case 'text_delta': {
        currentAssistant().text += (p.text as string) ?? '';
        break;
      }
      case 'tool_call': {
        const b = currentAssistant();
        const tool: ToolStep = { name: (p.name as string) ?? 'tool' };
        const summary = p.args_summary as string;
        if (summary) tool.argsSummary = summary;
        if (p.args_full !== undefined) tool.argsFull = p.args_full;
        (b.tools ||= []).push(tool);
        break;
      }
      case 'tool_result': {
        const b = currentAssistant();
        const t = b.tools && b.tools[b.tools.length - 1];
        if (t) {
          t.result = (p.summary as string) ?? '';
          if (typeof p.output_full === 'string') t.outputFull = p.output_full;
          t.isError = p.is_error === true;
        }
        break;
      }
      case 'final': {
        const b = currentAssistant();
        // Trust final.text as canonical (flattens any missed delta).
        b.text = (p.text as string) ?? b.text;
        b.streaming = false;
        loading.value = false;
        break;
      }
      case 'error': {
        const msg = (p.message as string) ?? (p.error as string) ?? '';
        // Drop an empty streaming placeholder so we don't leave a blank bubble.
        const last = messages.value[messages.value.length - 1];
        if (last && last.role === 'assistant' && last.streaming && !last.text) {
          messages.value.pop();
        }
        if (msg === 'context_full') {
          full.value = true;
          error.value = 'This quick chat is full. Continue in Studio to keep going.';
        } else {
          error.value = msg || 'Something went wrong. Try again.';
        }
        loading.value = false;
        break;
      }
      // inbound (our own echo), status, agent_active, vm_phase, done, stale:
      // not needed for the compact popover - the journal stays authoritative.
      default: break;
    }
  }

  function attachStream(): EventSource | null {
    if (sse || !conversationId.value) return sse;
    const es = new EventSource(wsUrl(`/v1/api/conversations/${conversationId.value}/stream?scope=user`), {
      withCredentials: true,
    });
    for (const kind of ['text_delta', 'tool_call', 'tool_result', 'final', 'error']) {
      es.addEventListener(kind, (ev) => onEvent(kind, ev as MessageEvent));
    }
    // EventSource auto-reconnects (Last-Event-ID); nothing to do on error.
    sse = es;
    return es;
  }

  // Drop the empty streaming assistant placeholder so a failure doesn't leave a
  // blank "Q" bubble with a stuck cursor. A synchronous POST failure produces no
  // SSE `error` event (whose handler does the same), so we must pop it here too.
  function dropEmptyPlaceholder(): void {
    const last = messages.value[messages.value.length - 1];
    if (last && last.role === 'assistant' && last.streaming && !last.text) {
      messages.value.pop();
    }
  }

  async function postMessage(text: string): Promise<void> {
    const id = conversationId.value;
    if (!id) return;
    try {
      const res = await fetch(wsUrl(`/v1/api/conversations/${id}/messages`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, idempotency_key: crypto.randomUUID() }),
      });
      if (!res.ok) {
        // 402 = out of credits: show the server's upgrade message, not a bare
        // status code. The body is the plain-text OUT_OF_CREDITS_MSG.
        const body = await res.text().catch(() => '');
        error.value = res.status === 402 && body ? body : `Send failed (${res.status}).`;
        loading.value = false;
        dropEmptyPlaceholder();
      }
    } catch {
      error.value = 'Send failed. Try again.';
      loading.value = false;
      dropEmptyPlaceholder();
    }
  }

  async function send(text: string): Promise<void> {
    const q = text.trim();
    if (!q || loading.value) return;
    error.value = null;
    // Claim the turn synchronously: the mint POST below is awaited, so without
    // this a rapid second Enter would pass the guard and mint a second fast
    // conversation (orphaning the first). Reset it on the mint-failure path.
    loading.value = true;

    // Mint the fast conversation on first send.
    if (!conversationId.value) {
      try {
        const r = await api.post<{ conversation_id: string }>('/v1/api/ask', {});
        conversationId.value = r.conversation_id;
      } catch (e) {
        // 402 = out of credits: surface the server's upgrade message.
        const obj = e as { status?: number; error?: string };
        error.value = obj.status === 402 && obj.error
          ? obj.error
          : 'Couldn’t start quick chat. Try again.';
        loading.value = false;
        return;
      }
    }

    messages.value.push({ role: 'user', text: q });
    messages.value.push({ role: 'assistant', text: '', streaming: true, tools: [] });

    // Attach the stream BEFORE submitting so no early event is missed. For the
    // first turn we wait for the socket to open; afterwards it's already live.
    if (!sse) {
      const es = attachStream();
      let posted = false;
      const fire = () => { if (posted) return; posted = true; void postMessage(q); };
      es?.addEventListener('open', fire, { once: true });
      // Fallback if `open` is slow/never fires - `final` still flattens text.
      setTimeout(fire, 500);
    } else {
      void postMessage(q);
    }
  }

  // Promote the underlying fast conversation to a full hq. Returns the
  // conversation id to navigate to on success, or null when there's nothing
  // to promote / the call failed.
  async function promote(): Promise<string | null> {
    const id = conversationId.value;
    if (!id) return null;
    try {
      await api.post(`/v1/api/fast/${id}/promote`, {});
      return id;
    } catch {
      return null;
    }
  }

  return {
    open, messages, conversationId, loading, error, full,
    openPanel, closePanel, toggle, reset, send, promote,
  };
});
